import { useMemo, useState } from "react";
import type { Booking } from "@/lib/bookings";

type Grain = "daily" | "weekly" | "monthly";

const GRAINS: { key: Grain; label: string }[] = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const SERIES = [
  { key: "confirmed", label: "Confirmed", bar: "bg-primary-500" },
  { key: "new", label: "Pending", bar: "bg-accent-500" },
  { key: "completed", label: "Completed", bar: "bg-secondary-500" },
  { key: "cancelled", label: "Cancelled", bar: "bg-foreground-400" },
];

interface Bucket {
  label: string;
  start: number;
  end: number;
}

function buildBuckets(grain: Grain): Bucket[] {
  const buckets: Bucket[] = [];
  const now = new Date();

  if (grain === "daily") {
    for (let i = 13; i >= 0; i -= 1) {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
      buckets.push({
        label: start.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }
    return buckets;
  }

  if (grain === "weekly") {
    const monday = new Date(now);
    const offset = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - offset);
    monday.setHours(0, 0, 0, 0);
    for (let i = 11; i >= 0; i -= 1) {
      const start = new Date(monday);
      start.setDate(monday.getDate() - i * 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      buckets.push({
        label: start.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }
    return buckets;
  }

  for (let i = 11; i >= 0; i -= 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    buckets.push({
      label: start.toLocaleDateString("en-GB", { month: "short" }),
      start: start.getTime(),
      end: end.getTime(),
    });
  }
  return buckets;
}

export default function BookingsAnalytics({ bookings }: { bookings: Booking[] }) {
  const [grain, setGrain] = useState<Grain>("daily");

  const { buckets, statusTotals, maxTotal } = useMemo(() => {
    const built = buildBuckets(grain).map((bucket) => {
      const inside = bookings.filter((booking) => {
        const time = new Date(booking.created_at).getTime();
        return time >= bucket.start && time < bucket.end;
      });
      const counts: Record<string, number> = {};
      SERIES.forEach((series) => {
        counts[series.key] = inside.filter((booking) => booking.status === series.key).length;
      });
      const total = inside.length;
      return { ...bucket, counts, total };
    });

    const totals: Record<string, number> = {};
    SERIES.forEach((series) => {
      totals[series.key] = built.reduce((sum, bucket) => sum + bucket.counts[series.key], 0);
    });

    return {
      buckets: built,
      statusTotals: totals,
      maxTotal: Math.max(1, ...built.map((bucket) => bucket.total)),
    };
  }, [bookings, grain]);

  return (
    <section className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-foreground-950 font-semibold">Bookings over time</h2>
          <p className="text-sm text-foreground-600 mt-1">
            Every enquiry from your backend, grouped by when it arrived.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 self-start">
          {GRAINS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setGrain(option.key)}
              className={`text-xs md:text-sm font-semibold px-3.5 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                grain === option.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-end gap-1.5 h-52">
        {buckets.map((bucket) => {
          const heightPct = bucket.total === 0 ? 0 : Math.max(6, (bucket.total / maxTotal) * 100);
          return (
            <div key={bucket.label + bucket.start} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
              <span className="text-[10px] text-foreground-500 mb-1 h-4">
                {bucket.total > 0 ? bucket.total : ""}
              </span>
              <div
                className="w-full max-w-[46px] mx-auto rounded-t overflow-hidden flex flex-col-reverse"
                style={{ height: `${heightPct}%` }}
                title={`${bucket.total} bookings`}
              >
                {SERIES.map((series) =>
                  bucket.counts[series.key] > 0 ? (
                    <div
                      key={series.key}
                      className={`w-full ${series.bar}`}
                      style={{ flexGrow: bucket.counts[series.key] }}
                    ></div>
                  ) : null,
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-1.5 mt-2">
        {buckets.map((bucket) => (
          <div
            key={`label-${bucket.label}-${bucket.start}`}
            className="flex-1 text-[10px] text-foreground-500 text-center truncate"
          >
            {bucket.label}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 pt-4 border-t border-background-200">
        {SERIES.map((series) => (
          <span key={series.key} className="inline-flex items-center gap-2 text-xs text-foreground-700">
            <span className={`w-2.5 h-2.5 rounded-sm ${series.bar}`}></span>
            {series.label}
            <span className="font-semibold text-foreground-950">{statusTotals[series.key]}</span>
          </span>
        ))}
      </div>
    </section>
  );
}